import React, { useState, useEffect } from 'react';
import { Sparkles, Save, Power, Cloud, Server, Wifi, KeyRound, RefreshCw, FlaskConical } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const LOCAL_MODEL_OPTIONS = [
  { value: 'qwen2.5:7b-instruct', label: 'Qwen 2.5 7B Instruct (מומלץ — ציות הוראות מצוין)' },
  { value: 'dictalm2.0-instruct', label: 'DictaLM 2.0 Instruct (עברית טבעית ביותר)' },
  { value: 'gemma2:9b', label: 'Gemma 2 9B (דורש ~7GB RAM)' },
  { value: 'aya:8b', label: 'Aya 23 8B (רב-לשוני)' },
  { value: 'llama3.1:8b', label: 'Llama 3.1 8B' }
];

const MODEL_OPTIONS = [
  { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash-Lite (מומלץ)' },
  { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash Preview' },
  { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro Preview' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
  { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' }
];

export default function GeminiTab() {
  const [settings, setSettings] = useState({
    enabled: false,
    api_key: '',
    model: 'gemini-3.1-flash-lite',
    prompt: '',
    provider: 'gemini',
    local_url: 'http://localhost:11434',
    local_model: 'qwen2.5:7b-instruct',
    gemini_fallback_enabled: true,
    local_timeout_action: 'passthrough'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingKey, setTestingKey] = useState(false);
  const [keyResult, setKeyResult] = useState(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [googleModels, setGoogleModels] = useState(null);
  const [testingModel, setTestingModel] = useState(false);
  const [modelResult, setModelResult] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/gemini/settings');
      setSettings({
        enabled: false,
        api_key: '',
        model: 'gemini-3.1-flash-lite',
        prompt: '',
        provider: 'gemini',
        local_url: 'http://localhost:11434',
        local_model: 'qwen2.5:7b-instruct',
        gemini_fallback_enabled: true,
        local_timeout_action: 'passthrough',
        ...response.data
      });
    } catch (error) {
      toast.error('שגיאה בטעינת הגדרות');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.put('/gemini/settings', settings);
      toast.success('הגדרות נשמרו בהצלחה');
    } catch (error) {
      toast.error('שגיאה בשמירת הגדרות');
    } finally {
      setSaving(false);
    }
  };

  const handleTestLocal = async () => {
    setTesting(true);
    try {
      const response = await api.post('/gemini/test-local', {
        local_url: settings.local_url,
        local_model: settings.local_model
      });
      if (response.data.ok) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message, { duration: 6000 });
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'שגיאה בבדיקת החיבור ל-Ollama';
      toast.error(msg, { duration: 6000 });
    } finally {
      setTesting(false);
    }
  };

  const handleTestKey = async () => {
    setTestingKey(true);
    setKeyResult(null);
    try {
      const response = await api.post('/gemini/test-key', { api_key: settings.api_key, model: settings.model });
      setKeyResult(response.data);
    } catch (error) {
      setKeyResult({ ok: false, message: error.response?.data?.message || 'שגיאה בבדיקת המפתח' });
    } finally {
      setTestingKey(false);
    }
  };

  const handleLoadModels = async () => {
    setLoadingModels(true);
    try {
      const response = await api.post('/gemini/list-models', { api_key: settings.api_key });
      setGoogleModels(response.data.models);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בשליפת המודלים', { duration: 6000 });
    } finally {
      setLoadingModels(false);
    }
  };

  const handleTestModel = async () => {
    setTestingModel(true);
    setModelResult(null);
    try {
      const response = await api.post('/gemini/test-model', {
        api_key: settings.api_key,
        model: settings.model,
        prompt: settings.prompt
      });
      setModelResult(response.data);
    } catch (error) {
      setModelResult({ ok: false, message: error.response?.data?.message || 'שגיאה בבדיקת המודל' });
    } finally {
      setTestingModel(false);
    }
  };

  const handleToggle = async () => {
    try {
      const response = await api.patch('/gemini/toggle');
      setSettings({ ...settings, enabled: response.data.enabled });
      toast.success(response.data.message);
    } catch (error) {
      toast.error('שגיאה בשינוי מצב');
    }
  };

  // רשימה שנשלפה מגוגל; המודל השמור נשאר ברשימה גם אם חסר בה
  const modelOptions = (() => {
    const base = googleModels || MODEL_OPTIONS;
    return settings.model && !base.some((o) => o.value === settings.model)
      ? [{ value: settings.model, label: settings.model }, ...base]
      : base;
  })();

  if (loading) {
    return <div className="text-center py-12">טוען הגדרות...</div>;
  }

  const apiEndpoint = `https://generativelanguage.googleapis.com/v1beta/models/${settings.model || 'gemini-3.1-flash-lite'}:generateContent?key=YOUR_API_KEY`;
  const payloadExample = `{
  "system_instruction": {
    "parts": [{
      "text": "[Prompt הסינון שלך]"
    }]
  },
  "contents": [{
    "role": "user",
    "parts": [{
      "text": "הנה הטקסט לבדיקה:\n[תוכן הידיעה]"
    }]
  }],
  "generationConfig": {
    "temperature": 0.1
  }
}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center">
          <Sparkles className="w-8 h-8 text-purple-600 ml-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">הגדרות Gemini AI</h2>
            <p className="text-gray-600 text-sm">סינון אוטומטי של תוכן באמצעות AI</p>
          </div>
        </div>
        
        <button
          onClick={handleToggle}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            settings.enabled
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Power className="w-5 h-5 ml-2" />
          {settings.enabled ? 'פעיל' : 'כבוי'}
        </button>
      </div>

      {/* Status Banner */}
      <div className={`rounded-lg p-4 ${
        settings.enabled ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
      }`}>
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full ml-3 ${settings.enabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
          <p className={`font-medium ${settings.enabled ? 'text-green-900' : 'text-gray-700'}`}>
            {settings.enabled 
              ? '✅ Gemini AI פעיל ומסנן הודעות' 
              : '⚪ Gemini AI כבוי - הודעות לא עוברות סינון AI'}
          </p>
        </div>
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSave} className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-6">
        {/* Provider Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            מנוע הסינון
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={async () => {
                const newSettings = { ...settings, provider: 'gemini' };
                setSettings(newSettings);
                try {
                  await api.put('/gemini/settings', newSettings);
                  toast.success('מנוע הסינון עבר ל-Gemini (ענן)');
                } catch (err) {
                  toast.error('שגיאה בשמירת מנוע הסינון');
                }
              }}
              className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                settings.provider === 'gemini'
                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Cloud className="w-5 h-5 ml-2" />
              Gemini (ענן)
            </button>
            <button
              type="button"
              onClick={async () => {
                const newSettings = { ...settings, provider: 'local' };
                setSettings(newSettings);
                try {
                  await api.put('/gemini/settings', newSettings);
                  toast.success('מנוע הסינון עבר למודל מקומי (Ollama)');
                } catch (err) {
                  toast.error('שגיאה בשמירת מנוע הסינון');
                }
              }}
              className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                settings.provider === 'local'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Server className="w-5 h-5 ml-2" />
              מודל מקומי (Ollama)
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            {settings.provider === 'local'
              ? 'הסינון ירוץ על שרת מקומי דרך Ollama — ללא עלות API. ודא ש-Ollama רץ והמודל הורד.'
              : 'הסינון ירוץ דרך Gemini API / Vertex AI בענן.'}
          </p>
        </div>

        {/* Local LLM fields */}
        {settings.provider === 'local' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                כתובת Ollama
              </label>
              <input
                type="text"
                value={settings.local_url || ''}
                onChange={(e) => setSettings({ ...settings, local_url: e.target.value })}
                placeholder="http://localhost:11434"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono"
                dir="ltr"
              />
              <p className="mt-2 text-sm text-gray-500">
                כתובת שירות ה-Ollama. ברירת מחדל: <code>http://localhost:11434</code>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                מודל מקומי
              </label>
              <select
                value={settings.local_model || 'qwen2.5:7b-instruct'}
                onChange={(e) => setSettings({ ...settings, local_model: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {LOCAL_MODEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-sm text-gray-500">
                הרץ בשרת: <code dir="ltr">ollama pull {settings.local_model || 'qwen2.5:7b-instruct'}</code>
              </p>
            </div>

            <div>
              <button
                type="button"
                onClick={handleTestLocal}
                disabled={testing}
                className="flex items-center px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {testing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                    בודק חיבור...
                  </>
                ) : (
                  <>
                    <Wifi className="w-5 h-5 ml-2" />
                    בדיקת חיבור ל-Ollama
                  </>
                )}
              </button>
              <p className="mt-2 text-sm text-gray-500">
                בודק ש-Ollama מגיב בכתובת שהוגדרה ושהמודל הנבחר הותקן.
              </p>
            </div>

            {/* Gemini fallback toggle */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <div className="pl-4">
                  <p className="text-sm font-medium text-gray-700">נפילה ל-Gemini בעומס</p>
                  <p className="text-sm text-gray-500">
                    כשהתור המקומי עמוס (מעל 30 הודעות בהמתנה), הודעות יופנו ל-Gemini כדי למנוע פיגור.
                    אם Gemini נכשל — ההודעה חוזרת לתור המקומי.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, gemini_fallback_enabled: !settings.gemini_fallback_enabled })}
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${
                    settings.gemini_fallback_enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={settings.gemini_fallback_enabled}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      settings.gemini_fallback_enabled ? 'translate-x-1' : 'translate-x-6'
                    }`}
                  />
                </button>
              </div>
              {!settings.gemini_fallback_enabled && (
                <p className="mt-2 text-sm text-amber-600">
                  ⚠️ הנפילה מושבתת — גם בעומס, הסינון יישאר מקומי בלבד ולא ייגש ל-Gemini (ייתכן פיגור בשיאים).
                </p>
              )}

              {/* התנהגות בטיימאאוט — רק כשהנפילה מושבתת */}
              {!settings.gemini_fallback_enabled && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">התנהגות כשהודעה ממתינה מעל 10 דקות</p>
                  <p className="text-sm text-gray-500 mb-3">
                    כשהתור המקומי עמוס והנפילה ל-Gemini מושבתת, הודעות שממתינות מעל 10 דקות יטופלו לפי ההגדרה:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={async () => {
                        const newSettings = { ...settings, local_timeout_action: 'passthrough' };
                        setSettings(newSettings);
                        try {
                          await api.put('/gemini/settings', newSettings);
                          toast.success('הודעות שפג תוקפן יעלו ללא סינון');
                        } catch (err) {
                          toast.error('שגיאה בשמירה');
                        }
                      }}
                      className={`flex items-center justify-center px-4 py-2.5 rounded-lg border-2 transition-colors text-sm ${
                        (settings.local_timeout_action || 'passthrough') === 'passthrough'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      עלה ללא סינון
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const newSettings = { ...settings, local_timeout_action: 'drop' };
                        setSettings(newSettings);
                        try {
                          await api.put('/gemini/settings', newSettings);
                          toast.success('הודעות שפג תוקפן ימחקו ולא יעלו');
                        } catch (err) {
                          toast.error('שגיאה בשמירה');
                        }
                      }}
                      className={`flex items-center justify-center px-4 py-2.5 rounded-lg border-2 transition-colors text-sm ${
                        settings.local_timeout_action === 'drop'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      מחק הודעה
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* API Key (Gemini only) */}
        {settings.provider !== 'local' && (
        <>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            API Key
          </label>
          <div className="flex gap-2">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={settings.api_key || ''}
              onChange={(e) => setSettings({ ...settings, api_key: e.target.value })}
              placeholder="הכנס Gemini API Key..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
              dir="ltr"
            />
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              {showApiKey ? 'הסתר' : 'הצג'}
            </button>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={handleTestKey}
              disabled={testingKey || !settings.api_key}
              className="flex items-center px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {testingKey ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                  מאמת מול גוגל...
                </>
              ) : (
                <>
                  <KeyRound className="w-5 h-5 ml-2" />
                  אמת מפתח מול גוגל
                </>
              )}
            </button>
            {keyResult && (
              <p className={`mt-2 text-sm rounded-lg p-3 break-words ${keyResult.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                {keyResult.message}
              </p>
            )}
          </div>
          <p className="mt-2 text-sm text-gray-500">
            קבל API Key מ-<a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google AI Studio</a>
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            מודל Gemini לסינון
          </label>
          <div className="flex gap-2 flex-wrap">
            <select
              value={settings.model || 'gemini-3.1-flash-lite'}
              onChange={(e) => setSettings({ ...settings, model: e.target.value })}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              dir="ltr"
            >
              {modelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleLoadModels}
              disabled={loadingModels || !settings.api_key}
              title="שלוף את רשימת המודלים מגוגל"
              className="flex items-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ml-2 ${loadingModels ? 'animate-spin' : ''}`} />
              רענן מגוגל
            </button>
            <button
              type="button"
              onClick={handleTestModel}
              disabled={testingModel || !settings.api_key}
              title="שלח הודעה אקראית עם הפרומפט למודל שנבחר"
              className="flex items-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {testingModel ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
              ) : (
                <FlaskConical className="w-4 h-4 ml-2" />
              )}
              בדיקה
            </button>
          </div>
          {modelResult && (
            <div className={`mt-3 rounded-lg p-3 text-sm space-y-2 ${modelResult.ok ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-800'}`}>
              <p className="font-medium break-words">{modelResult.message}{modelResult.ms ? ` (${modelResult.ms}ms)` : ''}</p>
              {modelResult.sample && (
                <p><span className="font-medium">הודעת בדיקה:</span> {modelResult.sample}</p>
              )}
              {modelResult.ok && (
                <p className="whitespace-pre-wrap"><span className="font-medium">תשובת המודל:</span> {modelResult.reply || '(ריקה)'}</p>
              )}
              {modelResult.usage && (
                <p className="text-xs opacity-75" dir="ltr">
                  tokens — prompt: {modelResult.usage.promptTokenCount}, output: {modelResult.usage.candidatesTokenCount}, finish: {modelResult.finish_reason}
                </p>
              )}
            </div>
          )}
          <p className="mt-2 text-sm text-gray-500">
            {googleModels
              ? `הרשימה נשלפה מגוגל (${googleModels.length} מודלים).`
              : 'לחץ "רענן מגוגל" כדי לשלוף את המודלים הזמינים למפתח שלך.'}
            {' '}ברירת המחדל המומלצת: <code>gemini-3.1-flash-lite</code>.
          </p>
        </div>
        </>
        )}

        {/* Prompt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Prompt להנחיות הסינון
          </label>
          <textarea
            value={settings.prompt || ''}
            onChange={(e) => setSettings({ ...settings, prompt: e.target.value })}
            rows={12}
            placeholder="הכנס הנחיות לסינון..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
          />
          <p className="mt-2 text-sm text-gray-500">
            ההנחיות שיקבעו איך Gemini יסנן הודעות
          </p>
        </div>

        {/* Save Button */}
        <button
          type="submit"
          disabled={saving}
          className="w-full flex justify-center items-center px-4 sm:px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
              שומר...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 ml-2" />
              שמור הגדרות
            </>
          )}
        </button>
      </form>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">💡 איך זה עובד?</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Gemini בודק כל הודעה לפי ההנחיות שהגדרת</li>
          <li>• הודעות שלא עומדות בקריטריונים נדחות אוטומטית</li>
          <li>• ניתן לערוך את ה-Prompt בכל עת</li>
          <li>• השירות פועל בזמן אמת עם כל הודעה חדשה</li>
        </ul>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">כתובת ה-API</h3>
          <input
            type="text"
            readOnly
            value={apiEndpoint}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white font-mono text-sm"
            dir="ltr"
          />
        </div>

        <div>
          <h3 className="font-semibold text-gray-900 mb-2">פורמט הבקשה שנשלח</h3>
          <textarea
            readOnly
            value={payloadExample}
            rows={12}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white font-mono text-sm"
            dir="ltr"
          />
        </div>
      </div>
    </div>
  );
}
